// Tela de Tabela de Preços (issue #7): edita o preço base OU o preço de um
// Cliente, em massa. O seletor "Tabela" escolhe o alvo.

import {
  precoBaseVigente,
  precoClienteVigente,
  precoVigente,
} from "../../domain/precificacao.ts";
import { formatReais } from "../../domain/money.ts";
import { hojeISO } from "../../domain/datas.ts";
import type { ItemPrecoComando } from "../../comandos/preco.ts";
import type { Store } from "../store.ts";
import { clear, el, tabelaPaginada, botao } from "../ui.ts";
import { criarAcaoComando } from "./_acao.ts";
import {
  abrirConfirmacao,
  botaoLimparFiltros,
  filtro,
  telaLista,
} from "./_cadastro.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista } from "./_lista-filtros.ts";
import { inputMoeda, type CampoMoeda } from "./_input-moeda.ts";
import { abrirHistoricoPreco } from "./_historico-preco.ts";
import { abrirFormTipoPeca } from "./_tipo-peca-form.ts";
import { abrirMatrizPrecos } from "./_matriz-precos.ts";
import { abrirExportarPrecos, abrirImportarPrecos } from "./_precos-io.ts";
import { podeEditarModulo } from "./_permissao.ts";

export function renderTabelaPrecos(
  container: HTMLElement,
  store: Store,
): () => void {
  // a tabela sendo editada (Preço Base ou um Cliente) — não é filtro da
  // lista: o "Limpar Filtros" não mexe nela
  let alvo = "base"; // "base" ou o id de um Cliente
  // busca e página num estado só (#93); ordena por Categoria
  const lista = new EstadoLista({}, { ordCol: 1 });
  // só entram aqui os tipos cujo valor mudou (centavos, ou `null` para "limpar").
  // sobrevive à troca de página / busca / alvo.
  let rascunho = new Map<string, number | null>();
  let inputs = new Map<string, { campo: CampoMoeda; inicial: number | null }>();
  const sincronizar = (): void => {
    for (const [id, { campo, inicial }] of inputs) {
      const atual = campo.centavos();
      if (atual !== inicial) rascunho.set(id, atual);
      else rascunho.delete(id);
    }
  };
  const busca = buscaDaLista(lista, "Buscar por tipo de peça…", () => {
    sincronizar();
    render();
  });

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "configuracao");
    const clientes = [...snap.clientes]
      .filter((c) => c.ativo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    if (alvo !== "base" && !clientes.some((c) => c.id === alvo)) alvo = "base";
    const modoBase = alvo === "base";

    const limparBtn = botaoLimparFiltros(() => {
      sincronizar();
      lista.limpar();
      render();
    });
    const importar = botao("secundario", {}, "Importar");
    importar.addEventListener("click", () => {
      sincronizar();
      abrirImportarPrecos(store, modoBase ? null : alvo, () => {
        rascunho = new Map();
        render();
      });
    });
    const exportar = botao("secundario", {}, "Exportar");
    exportar.addEventListener("click", () => abrirExportarPrecos(store));
    const matriz = botao("secundario", {}, "Ver Matriz");
    matriz.addEventListener("click", () => abrirMatrizPrecos(store));
    const novoTipo = botao(
      "secundario",
      {},
      "+ Novo Tipo de Peça",
    );
    novoTipo.addEventListener("click", () => {
      sincronizar();
      abrirFormTipoPeca(store, null);
    });

    const barra = el(
      "div",
      { class: "barra-filtros" },
      filtro(
        "Tabela",
        [
          ["base", "Preço Base"],
          ...clientes.map((c) => [c.id, `${c.codigo} · ${c.nome}`] as const),
        ],
        alvo,
        (v: string) => {
          sincronizar();
          rascunho = new Map();
          alvo = v;
          lista.tabela.page = 1;
          render();
        },
      ),
      limparBtn,
      el(
        "div",
        { class: "barra-filtros-fim" },
        matriz,
        ...(podeGravar ? [novoTipo, importar] : []),
        exportar,
      ),
    );

    const rerender = (): void => {
      sincronizar();
      render();
    };

    const catNome = new Map(snap.categorias.map((c) => [c.id, c.nome]));
    const tipos = [...snap.tiposPeca]
      .filter((t) => t.ativo)
      .filter((t) => busca.casa(t.nome, catNome.get(t.categoriaId)))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    const hoje = hojeISO();
    const erro = el("p", { class: "form-erro", role: "alert" });

    inputs = new Map();
    const linhas = tipos.map((t) => {
      const base = precoBaseVigente(snap, t.id, hoje);
      const doCliente = modoBase ? null : precoClienteVigente(snap, alvo, t.id, hoje);
      const inicial = (modoBase ? base : doCliente) ?? null;
      const mostrado = rascunho.has(t.id) ? (rascunho.get(t.id) ?? null) : inicial;
      const campo = inputMoeda({
        centavos: mostrado,
        ariaLabel: `Preço de ${t.nome}`,
      });
      campo.input.disabled = !podeGravar;
      inputs.set(t.id, { campo, inicial });

      const hist = botao(
        "link",
        { title: "Ver histórico deste preço" },
        "Histórico",
      );
      hist.addEventListener("click", () => abrirHistoricoPreco(store, t.id, modoBase ? null : alvo));

      const catTd = el("td", { class: "muted" }, catNome.get(t.categoriaId) ?? "—");

      if (modoBase) {
        return el(
          "tr",
          {},
          el("td", {}, t.nome),
          catTd,
          el("td", { class: "preco-cliente" }, campo.element),
          el("td", {}, hist),
        );
      }

      const efetivo = precoVigente(snap, alvo, t.id, hoje);
      const usarBase = botao(
        "secundario",
        { title: "Voltar ao preço base" },
        "Usar Base",
      );
      usarBase.addEventListener("click", () => {
        campo.setCentavos(null);
        campo.input.focus();
      });
      return el(
        "tr",
        {},
        el("td", {}, t.nome),
        catTd,
        el("td", { class: "muted" }, base != null ? formatReais(base) : "—"),
        el(
          "td",
          { class: "preco-cliente" },
          campo.element,
          podeGravar && doCliente != null ? usarBase : null,
        ),
        el(
          "td",
          { class: "muted" },
          efetivo.pendente
            ? "Pendente de precificação"
            : `${formatReais(efetivo.valorCentavos)} (${efetivo.origem === "cliente" ? "Cliente" : "Base"})`,
        ),
        el("td", {}, hist),
      );
    });

    const salvar = (): void => {
      erro.textContent = "";
      sincronizar();
      const destino = alvo;
      const itens: ItemPrecoComando[] = [];
      for (const [tipoId, centavos] of rascunho) {
        if (modoBase) {
          if (centavos != null) itens.push({ escopo: "base", tipoId, centavos });
        } else {
          itens.push({ escopo: "cliente", clienteId: destino, tipoId, centavos });
        }
      }
      if (itens.length === 0) return;
      // sem `desde`: a data vem do `ctx.hoje` do servidor (a grade não tem campo)
      criarAcaoComando(store, erro)(
        { agregado: "preco", cmd: { tipo: "aplicarTabela", itens } },
        undefined,
        () => {
          rascunho = new Map();
        },
      );
    };

    const botaoSalvar = botao(
      "primario",
      {},
      "Salvar Tabela",
    );
    botaoSalvar.addEventListener("click", salvar);

    // Botão provisório: some quando os preços estiverem acertados.
    const apagarTudo = botao(
      "perigo",
      {},
      "Apagar Todo o Histórico de Preços",
    );
    apagarTudo.addEventListener("click", () => {
      abrirConfirmacao({
        titulo: "Apagar Todo o Histórico de Preços?",
        mensagem:
          "Apaga TODO o histórico de preços — base e de todos os hotéis. Digite ZERAR para confirmar:",
        rotuloConfirmar: "Apagar Tudo",
        perigo: true,
        exigirDigitacao: "ZERAR",
        aoConfirmar: () => {
          rascunho = new Map();
          void store.executar(
            { agregado: "preco", cmd: { tipo: "limparTudo" } },
            "Histórico de preços apagado.",
          );
        },
      });
    });

    const colunas = modoBase
      ? [
          "Tipo de Peça",
          "Categoria",
          { t: "Preço base", ord: false as const, w: 200 },
          { t: "", ord: false as const, w: 96 },
        ]
      : [
          "Tipo de Peça",
          "Categoria",
          "Preço base",
          // w: 200 cortava o botão "Usar base" em telas mais estreitas que o
          // desktop (tablet, por ex.) — o excedente de largura que "sobra" e
          // estica as colunas em telas bem largas não existe mais aí, e o
          // campo + botão juntos precisam de mais que 200px de verdade.
          { t: "Preço deste cliente", ord: false as const, w: 240 },
          { t: "Efetivo hoje", ord: false as const, w: 190 },
          { t: "", ord: false as const, w: 96 },
        ];

    container.append(
      telaLista(
        barra,
        busca.elemento(),
        tabelaPaginada(
          modoBase ? "tabela-precos-base" : "tabela-precos",
          colunas,
          linhas,
          lista.tabela,
          rerender,
        ),
        podeGravar ? erro : null,
        podeGravar
          ? el("div", { class: "form-actions" }, botaoSalvar, apagarTudo)
          : null,
      ),
    );
  };

  render();
  return store.subscribe(render);
}
